import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DatamuseService from "../../../services/DatamuseService";
import ConfirmDialog from "../../components/ConfirmDialog";
import StepFooter from "../../pzl_common/board/StepFooter";
import PuzzleWorkflowStepper from "../../pzl_common/board/PuzzleWorkflowStepper";
import BorderHeader from "../../pzl_common/BorderHeader";
import StepPuzzleWordPanel from "../../pzl_common/board/StepPuzzleWordPanel";
import BorderLeftPanel from "../../pzl_common/BorderLeftPanel";
import AdminCrossBoardService, { DRAFT, REVIEWED } from "./AdminCrossBoardService";
import Logger from "../../../services/Logger";
import { usePuzzleData } from "./usePuzzleData";

export default function Step2ReadyToReview() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [refresh, setRefresh] = useState(0);
    const { crossTpl: puzzle, loading, error: fetchError } = usePuzzleData(id, refresh);
    const [error, setError] = useState(null);

    // Loading states
    const [loadingReview, setLoadingReview] = useState(false);
    const [loadingGoBackDraft, setLoadingGoBackDraft] = useState(false);
    const [loadingDiscard, setLoadingDiscard] = useState(false);
    const [loadingApplyTitleHook, setLoadingApplyTitleHook] = useState(false);

    // Confirmation dialog state for discarding template
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [showTitleHookDialog, setShowTitleHookDialog] = useState(false);


    // Function to initialize the square array
    const initializeSquare = (filledCoordinates) => {
        const size = 12; // Assuming a 12x12 grid
        const newSquare = Array.from({ length: size }, () => Array.from({ length: size }, () => ""));

        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }

        return newSquare;
    };

    const findCharacter = (row, col, filledCoordinates) => {
        if (filledCoordinates) {
            for (const coord of filledCoordinates) {
                const matchingPosition = coord.positions.find(
                    (p) => p.row === row && p.col === col
                );
                if (matchingPosition && matchingPosition.value) {
                    return matchingPosition.value.toUpperCase();
                }
            }
        }
        return "";
    };

    // Parse puzzle grid from filledCoordinates
    const parsePuzzleGrid = () => {
        if (!puzzle?.filledCoordinates) return [];

        try {
            const filledCoordinates = JSON.parse(puzzle.filledCoordinates);
            return initializeSquare(filledCoordinates);
        } catch (error) {
            Logger.error('Error parsing filled coordinates:', error);
            return [];
        }
    };



    const handleReview = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingReview(true);
        
        try {
            setError(null);

            // Get word list from filledCoordinates
            const wordList = puzzle.filledCoordinates ? JSON.parse(puzzle.filledCoordinates).map(item => item.text) : [];

            // Get word scores from DatamuseService
            const wordScores = await DatamuseService.requestWordScores(wordList);

            // Call API to change state to REVIEWED with details
            await AdminCrossBoardService.updateCrossTplState(puzzle.id, REVIEWED, null, wordScores);
            navigate(`/admin/puzzle/cross_board/reviewed/${puzzle.id}`);
        } catch (err) {
            Logger.error("Error updating puzzle state:", err);
            setError("Failed to review puzzle. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingReview(false);
        }
    };

    const handleGoBackDraft = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingGoBackDraft(true);
        
        try {
            setError(null);
            // Call API to change state back to DRAFT
            await AdminCrossBoardService.updateCrossTplState(puzzle.id, DRAFT);
            // Navigate back to draft step
            navigate(`/admin/puzzle/cross_board/draft/${puzzle.id}`);
        } catch (err) {
            Logger.error("Error moving puzzle back to draft:", err);
            setError("Failed to move puzzle back to draft. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingGoBackDraft(false);
        }
    };

    const handleBack = () => {
        const fromStep = searchParams.get('fromStep');
        if (fromStep !== null) {
            navigate(`/admin/puzzle/cross_board?activeStep=${fromStep}`);
        } else {
            navigate("/admin/puzzle/cross_board");
        }
    };

    const handleApplyTitleHook = () => {
        setShowTitleHookDialog(true);
    };

    const confirmApplyTitleHook = async () => {
        if (!puzzle?.id) return;

        try {
            setLoadingApplyTitleHook(true);
            const result = await AdminCrossBoardService.applyTitleHook(puzzle.id);
            
            if (result.code && result.code === "FAILURE") {
                setError(result.message || "Failed to apply title hook");
            } else {
                setError(null);
                setRefresh(prev => prev + 1);
            }
        } catch (err) {
            Logger.error("Error applying title hook:", err);
            setError("Failed to apply title hook. Please try again.");
        } finally {
            setLoadingApplyTitleHook(false);
        }
    };

    const handleDiscardTemplate = () => {
        // Show confirmation dialog instead of immediately discarding
        setOpenConfirmDialog(true);
    };

    const handleConfirmDiscard = async (targetId) => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingDiscard(true);
        
        try {
            setError(null);
            // Call API to mark template as discarded
            await AdminCrossBoardService.markAsDiscarded(targetId);
            // Show success message
            // Note: You might want to add a snackbar component here
            // Navigate back to puzzle list
            navigate("/admin/puzzle/cross_board");
        } catch (err) {
            Logger.error("Error discarding template:", err);
            setError("Failed to discard template. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingDiscard(false);
            setOpenConfirmDialog(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
                p: 3
            }}>
                <Typography variant="h6">Loading puzzle data...</Typography>
            </Box>
        );
    }

    if (fetchError || !puzzle) {
        return (
            <Box sx={{ mt: 2, p: 3 }}>
                <Alert severity="error">
                    {fetchError || "Puzzle not found"}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            {/* Workflow Stepper */}
            <PuzzleWorkflowStepper
                currentState={puzzle?.state}
                puzzleId={puzzle?.id}
                size="small"
                align="left"
                onStepClick={() => setRefresh(prev => prev + 1)}
                puzzleType={puzzle?.type}
            />
            
            {/* Header with back button */}
            <BorderHeader
                onBack={handleBack}
                puzzle={puzzle}
                puzzleType="crossword"
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                {/* First Column: Puzzle Information */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <BorderLeftPanel
                        puzzle={puzzle}
                        onRefresh={() => setRefresh(prev => prev + 1)}
                        puzzleType="CROSSWORD"
                    />
                </Grid>

                {/* Second Column: Words List / Word Network */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <StepPuzzleWordPanel
                        puzzle={puzzle}
                        parsePuzzleGrid={parsePuzzleGrid}
                    />
                </Grid>

            </Grid>

            {/* Step Footer with Action Buttons */}
            <StepFooter
                stepType="ready-for-review"
                title="Ready to Review"
                description="Review the puzzle content and approve or send back for revisions."
                onDiscard={handleDiscardTemplate}
                onReview={handleReview}
                onGoBackDraft={handleGoBackDraft}
                onApplyTitleHook={handleApplyTitleHook}
                loadingReview={loadingReview}
                loadingGoBackDraft={loadingGoBackDraft}
                loadingDiscard={loadingDiscard}
                loadingApplyTitleHook={loadingApplyTitleHook}
            />

            {/* Confirmation Dialog for Discarding Template */}
            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Confirm Discard Template"
                message="Are you sure you want to discard this template? This action cannot be undone."
                targetName={`'${puzzle?.title || 'Untitled Template'}' will be discarded`}
                targetId={puzzle?.id}
                callback={handleConfirmDiscard}
            />

            {/* Confirmation Dialog for Hook The Title */}
            <ConfirmDialog
                open={showTitleHookDialog}
                setOpen={setShowTitleHookDialog}
                title="Hook The Title"
                message="Would you like to change the title using the Bedrock?"
                callback={confirmApplyTitleHook}
            />
        </Box>
    );
}

